import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AlertMessages } from '../AlertMessages';
import { AuthContext } from '../../contexts/Auth/AuthContext';
import { createStudent, getWalletStudents } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { StudentSearch } from '../common/StudentSearch';
import { formatCurrency } from '../../utils/formatCoins';
import { FormContainer } from './styles';

export const StudentForm = () => {
  const { user } = useContext(AuthContext);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [nfcCardId, setNfcCardId] = useState('');
  const [studentCode, setStudentCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const [resetKey, setResetKey] = useState(0);
  const studentsListLoadedRef = useRef(false);
  const isLoadingStudentsRef = useRef(false);
  const nfcInputRef = useRef(null);
  const nfcTimeoutRef = useRef(null);
  const isNfcReadingRef = useRef(false);
  const nfcBufferRef = useRef('');
  const lastNfcValueRef = useRef('');

  const isDuplicate = false;

  // Cleanup de timeouts ao desmontar
  useEffect(() => {
    return () => {
      if (nfcTimeoutRef.current) {
        clearTimeout(nfcTimeoutRef.current);
      }
    };
  }, []);

  // Gerar código do estudante automaticamente quando NFC ID é informado
  // Usar apenas o UUID do cartão, sem combinar com idSponte
  React.useEffect(() => {
    if (nfcCardId.trim()) {
      // Código = apenas o UUID do cartão NFC
      setStudentCode(nfcCardId.trim());
    } else {
      setStudentCode('');
    }
  }, [nfcCardId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevenir submit durante leitura NFC
    if (isNfcReadingRef.current) {
      return;
    }

    if (!selectedStudent || !nfcCardId.trim()) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Selecione um estudante e informe o UUID do cartão NFC',
      });
      return;
    }

    // Validar que o UUID tem tamanho mínimo razoável
    if (nfcCardId.trim().length < 4) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'UUID do cartão deve ter pelo menos 4 caracteres',
      });
      return;
    }

    if (!studentCode.trim()) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'UUID do cartão não foi informado corretamente',
      });
      return;
    }

    if (isDuplicate) {
      return;
    }

    try {
      setLoading(true);
      const result = await createStudent({
        name: selectedStudent.name,
        studentCode: studentCode.trim(),
        idSponte: selectedStudent.idSponte || selectedStudent.id || null,
        isActive,
      });

      setAlertState({
        show: true,
        type: 'success',
        message: `Estudante ${result.student.name} cadastrado com sucesso!`,
      });

      // Limpar formulário
      setSelectedStudent(null);
      setNfcCardId('');
      setStudentCode('');
      setIsActive(true);
      setResetKey(prev => prev + 1);

    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormContainer>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Instruções do fluxo */}
          <Box
            sx={{
              p: 2,
              bgcolor: '#e3f2fd',
              borderRadius: 2,
              border: '1px solid #90caf9',
            }}
          >
            <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600, mb: 1 }}>
              📋 Passo a passo:
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              1. Busque e selecione o estudante pelo nome
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              2. Aproxime ou leia o cartão NFC do estudante (apenas o UUID será salvo)
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              3. O UUID do cartão será usado como código do estudante
            </Typography>
          </Box>

          <StudentSearch
            key={resetKey}
            onSelect={(student) => {
              if (student) {
                setSelectedStudent({
                  name: student.Nome,
                  idSponte: student.AlunoID,
                  id: student.AlunoID
                });
              } else {
                setSelectedStudent(null);
              }
            }}
            label="Busque e selecione um estudante do sistema"
          />

          {selectedStudent && !isDuplicate && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#e8f5e9',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                Estudante selecionado: <strong>{selectedStudent.name}</strong>
              </Typography>
            </Box>
          )}

          {selectedStudent && isDuplicate && (
            <Alert severity="error">
              Este estudante já possui um cartão NFC cadastrado no sistema. Por favor, edite o cartão existente na lista abaixo se necessário.
            </Alert>
          )}

          <Box sx={{ position: 'relative' }}>
            <TextField
              inputRef={nfcInputRef}
              label="UUID do Cartão NFC"
              value={nfcCardId}
              onChange={(e) => {
                const newValue = e.target.value;

                // Atualizar o valor imediatamente para feedback visual
                setNfcCardId(newValue);

                // Limpar timeout anterior
                if (nfcTimeoutRef.current) {
                  clearTimeout(nfcTimeoutRef.current);
                }

                // Se o valor está aumentando rapidamente (mais de 1 caractere de diferença),
                // provavelmente é leitura NFC
                const valueDiff = newValue.length - (lastNfcValueRef.current.length || 0);
                const isRapidInput = valueDiff > 1 || (newValue.length > 0 && lastNfcValueRef.current.length === 0);

                if (isRapidInput && newValue.length > 0) {
                  // Marcar como leitura NFC em andamento
                  isNfcReadingRef.current = true;

                  // Aguardar um pouco para capturar o valor completo (leitor NFC pode enviar Enter no final)
                  nfcTimeoutRef.current = setTimeout(() => {
                    // Garantir que temos o valor completo
                    const finalValue = newValue.trim();
                    setNfcCardId(finalValue);
                    lastNfcValueRef.current = finalValue;
                    isNfcReadingRef.current = false;
                  }, 500); // Aumentar tempo para garantir captura completa
                } else {
                  // Digitação manual normal ou valor estável
                  lastNfcValueRef.current = newValue;
                  isNfcReadingRef.current = false;
                }
              }}
              onKeyDown={(e) => {
                // SEMPRE prevenir submit automático com Enter no campo NFC
                // O submit deve ser feito apenas pelo botão
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  // Se o valor estiver completo e não estiver lendo NFC, focar no botão
                  if (!isNfcReadingRef.current && nfcCardId.trim().length >= 4) {
                    // Não fazer nada - deixar o usuário clicar no botão manualmente
                  }
                  return false;
                }
              }}
              onKeyPress={(e) => {
                // Prevenir submit automático durante leitura NFC
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
              }}
              fullWidth
              required
              variant="outlined"
              placeholder="Aproxime o cartão ou digite o UUID (ex: 12345678-6793)"
              helperText={
                selectedStudent
                  ? "Aproxime o cartão NFC do estudante ou digite o UUID manualmente. Apenas o UUID será salvo."
                  : "Primeiro selecione um estudante acima"
              }
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <CreditCardIcon sx={{ color: '#1e958c', fontSize: 24 }} />
                  </Box>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1e958c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e958c',
                    borderWidth: 2,
                  },
                },
              }}
              autoFocus={!!selectedStudent && !nfcCardId}
            />
          </Box>

          {studentCode && (
            <Card
              sx={{
                bgcolor: '#e8f5e9',
                border: '2px solid #2e7d32',
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    Código Gerado com Sucesso!
                  </Typography>
                </Box>
                <TextField
                  label="Código do Estudante"
                  value={studentCode}
                  fullWidth
                  required
                  variant="outlined"
                  disabled
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'white',
                      fontWeight: 600,
                      fontSize: '16px',
                    },
                  }}
                  helperText="UUID do cartão NFC que será usado para identificar o estudante nas transações"
                />
              </CardContent>
            </Card>
          )}

          {!studentCode && selectedStudent && nfcCardId.trim() && (
            <Alert severity="info">
              Aguardando leitura do cartão NFC ou digite o ID manualmente
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={isActive ? 'Ativo' : 'Inativo'}
              color={isActive ? 'success' : 'default'}
              onClick={() => setIsActive(!isActive)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !selectedStudent || isDuplicate || !nfcCardId.trim()}
            sx={{
              bgcolor: '#1e958c',
              padding: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0b4f6c' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Cadastrar Estudante'}
          </Button>
        </Box>
      </FormContainer>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
